import React, { useState, useEffect } from 'react';
import { 
  Save, Building, Mail, Phone, MapPin, Hash, 
  CreditCard, Percent, FileText, AlertCircle, X, Check, 
  Users, Globe, User, Calculator, Calendar, Handshake, Info,
  Palette, RotateCcw
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL;

// ─── Colores preestablecidos ─────────────────────────────────────────────────
const PRESET_COLORS = [
  { label: 'Verde (defecto)', value: '#3cbf9a' },
  { label: 'Azul marino',     value: '#26428E' },
  { label: 'Lima',            value: '#CCDC00' },
  { label: 'Índigo',          value: '#4f46e5' },
  { label: 'Rojo',            value: '#ef4444' },
  { label: 'Naranja',         value: '#f97316' },
  { label: 'Púrpura',         value: '#9333ea' },
  { label: 'Rosa',            value: '#ec4899' },
  { label: 'Gris oscuro',     value: '#374151' },
  { label: 'Negro',           value: '#111827' },
];

// ─── Validador hex robusto ────────────────────────────────────────────────────
// Acepta #RGB (3 dígitos) y #RRGGBB (6 dígitos), mayúsculas y minúsculas
const isValidHex = (c) =>
  typeof c === 'string' && /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(c.trim());

// ─── Selector de color ────────────────────────────────────────────────────────
function ColorPickerField({ label, value, defaultColor = '#3cbf9a', onChange, helpText }) {
  const [showPresets, setShowPresets] = useState(false);
  const safeValue = isValidHex(value) ? value.trim() : defaultColor;

  return (
    <div className="relative">
      <label className="block text-sm font-bold text-gray-700 mb-2">
        <Palette className="w-4 h-4 inline mr-2" />
        {label}
      </label>
      <div className="flex items-center gap-2">
        {/* Muestra de color — abre presets */}
        <div
          className="w-10 h-10 rounded-lg border-2 border-gray-300 flex-shrink-0 cursor-pointer shadow-sm transform hover:scale-105 transition-transform"
          style={{ backgroundColor: safeValue }}
          onClick={() => setShowPresets(!showPresets)}
          title="Ver colores preestablecidos"
        />
        {/* Input nativo de color */}
        <input
          type="color"
          value={safeValue}
          onChange={(e) => onChange(e.target.value)}
          className="w-10 h-10 rounded cursor-pointer border border-gray-300 p-0.5 flex-shrink-0"
          title="Elegir color personalizado"
        />
        {/* Input de texto hex */}
        <input
          type="text"
          value={value || ''}
          maxLength={7}
          placeholder="#3cbf9a"
          onChange={(e) => {
            let v = e.target.value.trim();
            if (v && !v.startsWith('#')) v = `#${v}`;
            onChange(v);
          }}
          className={`flex-1 px-3 py-2 border-2 rounded-lg focus:outline-none font-mono text-sm transition-colors ${
            value && !isValidHex(value) ? 'border-red-400' : 'border-gray-300'
          }`}
          onFocus={(e) => (e.target.style.borderColor = '#3cbf9a')}
          onBlur={(e) =>
            (e.target.style.borderColor =
              value && !isValidHex(value) ? '#f87171' : '#e5e7eb')
          }
        />
        {/* Botón restablecer */}
        <button
          type="button"
          onClick={() => onChange(defaultColor)}
          className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          title={`Restablecer a ${defaultColor}`}
        >
          <RotateCcw size={16} />
        </button>
      </div>
      {/* Mensaje de error sólo si hay valor y es inválido */}
      {value && !isValidHex(value) && (
        <p className="text-red-500 text-xs mt-1">Formato hex inválido (ej: #3cbf9a o #ACD55D)</p>
      )}
      {helpText && <p className="text-xs text-gray-500 mt-1">{helpText}</p>}

      {/* Panel de presets */}
      {showPresets && (
        <div className="absolute z-20 top-full left-0 mt-2 bg-white border border-gray-200 rounded-xl shadow-xl p-3 w-72">
          <p className="text-xs font-semibold text-gray-600 mb-2 uppercase tracking-wide">
            Colores preestablecidos
          </p>
          <div className="grid grid-cols-5 gap-2">
            {PRESET_COLORS.map((c) => (
              <button
                key={c.value}
                type="button"
                onClick={() => { onChange(c.value); setShowPresets(false); }}
                className="group flex flex-col items-center"
                title={c.label}
              >
                <div
                  className={`w-8 h-8 rounded-lg border-2 transform transition-transform group-hover:scale-110 ${
                    safeValue === c.value ? 'border-gray-800 scale-110' : 'border-gray-200'
                  }`}
                  style={{ backgroundColor: c.value }}
                />
                <span className="text-[9px] text-gray-500 mt-1 leading-tight text-center">
                  {c.label}
                </span>
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => setShowPresets(false)}
            className="mt-3 w-full text-xs text-gray-500 hover:text-gray-700 py-1"
          >
            Cerrar
          </button>
        </div>
      )}
    </div>
  );
}

export default function NewClient({ onClientCreated }) {
  const [formData, setFormData] = useState({
    id: '',
    razon_social: '',
    alias: '',
    cif: '',
    calle: '',
    localidad: '',
    provincia: '',
    c_postal: '',
    telefono1: '',
    telefono2: '',
    email: '',
    mail_admin: '',
    mail_admin_2: '',
    fax: '',
    contacto: '',
    clinica_matriz: '',
    cuenta_contable: '',
    f_pago: '',
    dia_pago: '',
    comision: '0',
    modelo_negocio: '',
    modneg_detalle: '',
    ahorro: '0',
    iban: '',
    email_calendario: '',
    tipo_calendario: 'ninguno',
    color_primario: '#3cbf9a',
    color_secundario: '#1a73e8',
    gestion_stock: 'inactivo',
    pedidos_pendientes: false,
    gestion_albaranes: false,
    notif_incidencias: true,
    importe_min: '',
    importe_max: '',
  });

  const [clients, setClients] = useState([]);
  const [formasPago, setFormasPago] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [showClientSuggestions, setShowClientSuggestions] = useState(false);

  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [errors, setErrors] = useState({});
  const [loadingNextId, setLoadingNextId] = useState(true);

  useEffect(() => {
    loadClients();
    loadFormasPago();
    generateNextClientId();
  }, []);

  const checkIdExists = async (id) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/clientes/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      return response.ok;
    } catch (error) {
      console.error('Error verificando ID:', error);
      return false;
    }
  };

  const generateNextClientId = async () => {
    try {
      setLoadingNextId(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/clientes`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      let startId = 1;
      if (response.ok) {
        const data = await response.json();
        const clientes = data.clientes || [];
        let maxId = 0;
        clientes.forEach(cliente => {
          const clienteId = parseInt(cliente.id);
          if (!isNaN(clienteId) && clienteId > maxId && clienteId < 32767) maxId = clienteId;
        });
        startId = Math.max(maxId + 1, 1);
      }
      let candidateId = startId;
      let attempts = 0;
      while (attempts < 100) {
        if (candidateId > 32766) candidateId = 1;
        const exists = await checkIdExists(candidateId);
        if (!exists) {
          setFormData(prev => ({ ...prev, id: candidateId.toString() }));
          return;
        }
        candidateId++;
        attempts++;
      }
      throw new Error('No se pudo encontrar un ID disponible');
    } catch (error) {
      console.error('Error al generar ID de cliente:', error);
      const timestampId = Math.floor(Date.now() / 1000) % 32766;
      setFormData(prev => ({ ...prev, id: timestampId.toString() }));
      showMessage('Advertencia: Se generó un ID basado en timestamp', 'warning');
    } finally {
      setLoadingNextId(false);
    }
  };

  const loadClients = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/clientes`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setClients(data.clientes || []);
      }
    } catch (error) {
      console.error('Error al cargar clientes:', error);
    }
  };

  const loadFormasPago = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/formas-pago`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setFormasPago(data.formas_pago || []);
      }
    } catch (error) {
      console.error('Error al cargar formas de pago:', error);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
    if (field === 'clinica_matriz') {
      if (value.length > 0) {
        setFilteredClients(clients.filter(c => c.razon_social.toLowerCase().includes(value.toLowerCase())));
        setShowClientSuggestions(true);
      } else {
        setShowClientSuggestions(false);
      }
    }
    if (field === 'tipo_calendario' && value !== 'ninguno') {
      if (!formData.email_calendario) {
        setErrors(prev => ({ ...prev, email_calendario: 'Debes ingresar un email de calendario' }));
      }
    } else if (field === 'tipo_calendario' && value === 'ninguno') {
      setFormData(prev => ({ ...prev, email_calendario: '' }));
      setErrors(prev => ({ ...prev, email_calendario: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.razon_social.trim()) newErrors.razon_social = 'La razón social es requerida';
    if (!formData.cif.trim()) newErrors.cif = 'El CIF es requerido';
    if (!formData.calle.trim()) newErrors.calle = 'La calle es requerida';
    if (!formData.localidad.trim()) newErrors.localidad = 'La localidad es requerida';
    if (!formData.c_postal.trim()) newErrors.c_postal = 'El código postal es requerido';
    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'El email no es válido';
    if (formData.mail_admin && !/\S+@\S+\.\S+/.test(formData.mail_admin)) newErrors.mail_admin = 'El email admin no es válido';
    if (formData.mail_admin_2 && !/\S+@\S+\.\S+/.test(formData.mail_admin_2)) newErrors.mail_admin_2 = 'El email admin 2 no es válido';

    if (formData.c_postal && (formData.c_postal.length !== 5 || isNaN(formData.c_postal))) {
      newErrors.c_postal = 'El código postal debe tener 5 dígitos';
    }
    if (formData.tipo_calendario !== 'ninguno') {
      if (!formData.email_calendario?.trim()) {
        newErrors.email_calendario = 'El email de calendario es requerido para sincronizar';
      } else if (!/\S+@\S+\.\S+/.test(formData.email_calendario)) {
        newErrors.email_calendario = 'El email de calendario no es válido';
      } else {
        const domain = formData.email_calendario.split('@')[1]?.toLowerCase();
        if (formData.tipo_calendario === 'google' && !domain?.includes('gmail.com') && !domain?.includes('googlemail.com')) {
          newErrors.email_calendario = 'Para Google Calendar, usa un email @gmail.com';
        } else if (formData.tipo_calendario === 'outlook' && !domain?.includes('outlook.com') && !domain?.includes('hotmail.com') && !domain?.includes('live.com')) {
          newErrors.email_calendario = 'Para Outlook Calendar, usa un email @outlook.com, @hotmail.com o @live.com';
        } else if (formData.tipo_calendario === 'icloud' && !domain?.includes('icloud.com') && !domain?.includes('me.com')) {
          newErrors.email_calendario = 'Para iCloud Calendar, usa un email @icloud.com o @me.com';
        }
      }
    }
    // Validación de colores — usa la función robusta
    if (formData.color_primario && !isValidHex(formData.color_primario)) {
      newErrors.color_primario = 'Color primario inválido (formato hex, ej: #3cbf9a o #ACD55D)';
    }
    if (formData.color_secundario && !isValidHex(formData.color_secundario)) {
      newErrors.color_secundario = 'Color secundario inválido (formato hex, ej: #1a73e8 o #003586)';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      showMessage('Por favor corrige los errores en el formulario', 'danger');
      return;
    }
    const clientId = parseInt(formData.id);
    if (!formData.id || isNaN(clientId) || clientId <= 0 || clientId > 32766) {
      showMessage('Error: ID de cliente inválido. Regenerando...', 'danger');
      generateNextClientId();
      return;
    }
    const idExists = await checkIdExists(clientId);
    if (idExists) {
      showMessage('El ID generado ya existe. Generando uno nuevo...', 'warning');
      generateNextClientId();
      return;
    }
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      let clinicaMatrizId = null;
      if (formData.clinica_matriz) {
        const client = clients.find(c => c.razon_social === formData.clinica_matriz);
        clinicaMatrizId = client ? client.id : null;
      }
      const clientData = {
        id: clientId,
        razon_social: formData.razon_social,
        alias: formData.alias,
        cif: formData.cif,
        calle: formData.calle,
        localidad: formData.localidad,
        provincia: formData.provincia,
        // String, NO parseInt: así se conservan ceros a la izquierda (p. ej. "01234").
        c_postal: String(formData.c_postal || '').trim(),
        telefono1: formData.telefono1,
        telefono2: formData.telefono2,
        mail: formData.email,
        mail_admin: formData.mail_admin,
        mail_admin_2: formData.mail_admin_2,
        fax: formData.fax,
        contacto: formData.contacto,
        clinica_matriz: clinicaMatrizId || 0,
        cuenta_contable: formData.cuenta_contable,
        forma_pago: parseInt(formData.f_pago) || 0,
        dia_pago: formData.dia_pago,
        comision: parseFloat(formData.comision) || 0,
        modelo_negocio: formData.modelo_negocio,
        modneg_detalle: formData.modneg_detalle,
        ahorro: parseFloat(formData.ahorro) || 0,
        iban: formData.iban,
        email_calendario: formData.email_calendario,
        tipo_calendario: formData.tipo_calendario,
        color_primario: formData.color_primario || '#3cbf9a',
        color_secundario: formData.color_secundario || '#1a73e8',
        gestion_stock: formData.gestion_stock || 'inactivo',
        pedidos_pendientes: !!formData.pedidos_pendientes,
        gestion_albaranes: !!formData.gestion_albaranes,
        notif_incidencias: !!formData.notif_incidencias,
        // Límite de gasto a nivel cliente (opcional). Vacío → null (sin techo);
        // el backend normaliza el string. importe_max es el techo que dispara
        // aprobación del supervisor si un pedido lo supera.
        importe_min: formData.importe_min,
        importe_max: formData.importe_max,
      };
      const response = await fetch(`${API_URL}/api/clientes`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(clientData)
      });
      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = 'Error del servidor al crear cliente';
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.message || errorMessage;
          if (errorMessage.includes('código de cliente ya existe') || errorMessage.includes('duplicate') || errorMessage.includes('exists')) {
            showMessage('Conflicto de ID detectado. Regenerando automáticamente...', 'warning');
            generateNextClientId();
            return;
          }
        } catch (e) {
          console.error('Error parsing response:', errorText);
        }
        throw new Error(errorMessage);
      }
      const responseData = await response.json().catch(() => ({}));
      const herencia = responseData.herencia_matriz;
      if (herencia && (herencia.proveedores_anadidos > 0 || herencia.precios_anadidos > 0)) {
        showMessage(
          `Cliente creado correctamente. Heredados de la clínica matriz: ` +
          `${herencia.proveedores_anadidos} proveedor(es), ${herencia.precios_anadidos} precio(s).`,
          'success'
        );
      } else {
        showMessage('Cliente creado correctamente', 'success');
      }
      setFormData({
        id: '', razon_social: '', alias: '', cif: '', calle: '', localidad: '', provincia: '',
        c_postal: '', telefono1: '', telefono2: '', email: '', mail_admin: '', mail_admin_2: '',
        fax: '', contacto: '', clinica_matriz: '', cuenta_contable: '', f_pago: '', dia_pago: '',
        comision: '0', modelo_negocio: '', modneg_detalle: '', ahorro: '0', iban: '',
        email_calendario: '', tipo_calendario: 'ninguno',
        color_primario: '#3cbf9a', color_secundario: '#1a73e8',
        gestion_stock: 'inactivo', pedidos_pendientes: false, gestion_albaranes: false,
        notif_incidencias: true,
        importe_min: '', importe_max: '',
      });
      generateNextClientId();
      if (onClientCreated) {
        setTimeout(() => onClientCreated(clientData), 1500);
      }
    } catch (error) {
      console.error('Error al crear cliente:', error);
      showMessage(error.message || 'Error al crear el cliente', 'danger');
    } finally {
      setSaving(false);
    }
  };

  const showMessage = (text, type) => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: '' }), 8000);
  };

  const getProvincias = () => [
    "Álava","Albacete","Alicante","Almería","Asturias","Ávila","Badajoz","Barcelona",
    "Burgos","Cáceres","Cádiz","Cantabria","Castellón","Ciudad Real","Córdoba","Cuenca",
    "Gerona","Granada","Guadalajara","Guipúzcoa","Huelva","Huesca","Islas Baleares",
    "Jaén","La Coruña","La Rioja","Las Palmas","León","Lérida","Lugo","Madrid","Málaga",
    "Murcia","Navarra","Orense","Palencia","Pontevedra","Salamanca","Santa Cruz de Tenerife",
    "Segovia","Sevilla","Soria","Tarragona","Teruel","Toledo","Valencia","Valladolid",
    "Vizcaya","Zamora","Zaragoza"
  ];

  const getModelosNegocio = () => [
    { value: 'FIJO_180', label: 'FIJO 180€ + IVA' },
    { value: 'FIJO_40',  label: 'FIJO 40€ + IVA' },
    { value: 'FIJO_100', label: 'FIJO 100€ + IVA' },
    { value: 'FIJO_300', label: 'FIJO 300€ + IVA' },
    { value: 'COMISION_TRANSACCIONES', label: 'COMISION TRANSACCIONES' },
    { value: 'COMISION_AHORRO',        label: 'COMISION AHORRO' },
    { value: 'ESCALADO',               label: 'ESCALADO' }
  ];

  const getTiposCalendario = () => [
    { value: 'ninguno',  label: 'Sin sincronización' },
    { value: 'google',   label: 'Google Calendar' },
    { value: 'outlook',  label: 'Outlook Calendar' },
    { value: 'icloud',   label: 'iCloud Calendar' }
  ];

  // ── Input helper ─────────────────────────────────────────────────────────────
  const Field = ({ label, icon: Icon, children, error, required }) => (
    <div>
      <label className="block text-sm font-bold text-gray-700 mb-2">
        {Icon && <Icon className="w-4 h-4 inline mr-2" />}{label}{required && '*'}
      </label>
      {children}
      {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
    </div>
  );

  const inputCls = (err) =>
    `w-full px-3 py-2 border-2 rounded-lg focus:outline-none transition-all ${err ? 'border-red-500' : 'border-gray-300'}`;

  const focusGreen = (e) => (e.target.style.borderColor = '#3cbf9a');
  const blurGreen  = (err) => (e) => (e.target.style.borderColor = err ? '#ef4444' : '#e5e7eb');

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        {/* ── Header ──────────────────────────────────────────────────────── */}
        <div className="px-6 py-4 border-b border-gray-200" style={{ backgroundColor: 'rgba(60,191,154,0.05)' }}>
          <div className="flex items-center space-x-4">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center shadow-lg" style={{ backgroundColor: '#3cbf9a' }}>
              <Building className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-800">Nuevo Cliente</h2>
              <p className="text-sm" style={{ color: '#3cbf9a' }}>Crear nueva cuenta de cliente</p>
            </div>
          </div>
        </div>

        {/* ── Mensaje de estado ────────────────────────────────────────────── */}
        {message.text && (
          <div className="px-6 pt-4">
            <div
              className={`p-3 rounded-lg flex items-center shadow-lg border text-sm ${
                message.type === 'success' ? 'border-emerald-200' : message.type === 'warning' ? 'border-yellow-200' : 'border-red-200'
              }`}
              style={{
                background: message.type === 'success' ? 'linear-gradient(135deg,#ecfdf5,#d1fae5)' : message.type === 'warning' ? 'linear-gradient(135deg,#fffbeb,#fef3c7)' : 'linear-gradient(135deg,#fef2f2,#fecaca)',
                color: message.type === 'success' ? '#065f46' : message.type === 'warning' ? '#92400e' : '#991b1b'
              }}
            >
              {message.type === 'success' ? <Check className="w-4 h-4 mr-2 flex-shrink-0" /> : <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0" />}
              <span className="font-medium flex-1">{message.text}</span>
              <button onClick={() => setMessage({ text: '', type: '' })} className="ml-auto p-1 hover:bg-white hover:bg-opacity-30 rounded"><X className="w-3 h-3" /></button>
            </div>
          </div>
        )}

        {/* ── Nota de token ────────────────────────────────────────────────── */}
        <div className="px-6 pt-4">
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-start">
            <Info className="w-5 h-5 text-blue-600 mr-3 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-blue-800">
              <strong>Nota:</strong> Una vez creado el cliente, podrá generar un token de calendario público
              desde la sección de gestión de clientes para permitir que los pacientes soliciten citas a través
              de un iframe embebido en su sitio web.
            </p>
          </div>
        </div>

        {/* ── Formulario ───────────────────────────────────────────────────── */}
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">

            {/* ── Información Básica ─────────────────────────────────────── */}
            <div className="md:col-span-3">
              <h3 className="text-lg font-bold text-gray-800 mb-4 border-b-2 pb-2" style={{ borderColor: '#3cbf9a' }}>Información Básica</h3>
            </div>

            {/* ID */}
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2"><Hash className="w-4 h-4 inline mr-2" />ID Cliente</label>
              <input type="text" value={loadingNextId ? 'Generando...' : formData.id} disabled className="w-full px-3 py-2 border-2 rounded-lg bg-gray-100 text-gray-600 text-sm" />
            </div>

            {/* Razón Social */}
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2"><Building className="w-4 h-4 inline mr-2" />Razón Social*</label>
              <input type="text" value={formData.razon_social} onChange={(e) => handleInputChange('razon_social', e.target.value)} placeholder="Nombre de la empresa"
                className={inputCls(errors.razon_social)} onFocus={focusGreen} onBlur={blurGreen(errors.razon_social)} />
              {errors.razon_social && <p className="text-red-500 text-xs mt-1">{errors.razon_social}</p>}
            </div>

            {/* Alias */}
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2"><FileText className="w-4 h-4 inline mr-2" />Alias</label>
              <input type="text" value={formData.alias} onChange={(e) => handleInputChange('alias', e.target.value)} placeholder="Nombre corto o alias"
                className={inputCls(false)} onFocus={focusGreen} onBlur={blurGreen(false)} />
            </div>

            {/* CIF */}
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2"><FileText className="w-4 h-4 inline mr-2" />CIF*</label>
              <input type="text" value={formData.cif} onChange={(e) => handleInputChange('cif', e.target.value)} placeholder="A12345678"
                className={inputCls(errors.cif)} onFocus={focusGreen} onBlur={blurGreen(errors.cif)} />
              {errors.cif && <p className="text-red-500 text-xs mt-1">{errors.cif}</p>}
            </div>

            {/* ── Dirección ──────────────────────────────────────────────── */}
            <div className="md:col-span-3">
              <h3 className="text-lg font-bold text-gray-800 mb-4 border-b-2 pb-2 mt-4" style={{ borderColor: '#3cbf9a' }}>Dirección</h3>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-bold text-gray-700 mb-2"><MapPin className="w-4 h-4 inline mr-2" />Calle*</label>
              <input type="text" value={formData.calle} onChange={(e) => handleInputChange('calle', e.target.value)} placeholder="Dirección completa"
                className={inputCls(errors.calle)} onFocus={focusGreen} onBlur={blurGreen(errors.calle)} />
              {errors.calle && <p className="text-red-500 text-xs mt-1">{errors.calle}</p>}
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2"><Hash className="w-4 h-4 inline mr-2" />Código Postal*</label>
              <input type="text" maxLength="5" value={formData.c_postal} onChange={(e) => handleInputChange('c_postal', e.target.value)} placeholder="28001"
                className={inputCls(errors.c_postal)} onFocus={focusGreen} onBlur={blurGreen(errors.c_postal)} />
              {errors.c_postal && <p className="text-red-500 text-xs mt-1">{errors.c_postal}</p>}
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2"><MapPin className="w-4 h-4 inline mr-2" />Localidad*</label>
              <input type="text" value={formData.localidad} onChange={(e) => handleInputChange('localidad', e.target.value)} placeholder="Ciudad o localidad"
                className={inputCls(errors.localidad)} onFocus={focusGreen} onBlur={blurGreen(errors.localidad)} />
              {errors.localidad && <p className="text-red-500 text-xs mt-1">{errors.localidad}</p>}
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2"><Globe className="w-4 h-4 inline mr-2" />Provincia</label>
              <select value={formData.provincia} onChange={(e) => handleInputChange('provincia', e.target.value)}
                className="w-full px-3 py-2 border-2 rounded-lg focus:outline-none transition-all bg-white border-gray-300"
                onFocus={focusGreen} onBlur={(e) => (e.target.style.borderColor = '#e5e7eb')}>
                <option value="">Seleccionar provincia</option>
                {getProvincias().map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>

            {/* ── Contacto ───────────────────────────────────────────────── */}
            <div className="md:col-span-3">
              <h3 className="text-lg font-bold text-gray-800 mb-4 border-b-2 pb-2 mt-4" style={{ borderColor: '#3cbf9a' }}>Información de Contacto</h3>
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2"><Phone className="w-4 h-4 inline mr-2" />Teléfono 1</label>
              <input type="tel" value={formData.telefono1} onChange={(e) => handleInputChange('telefono1', e.target.value)} placeholder="912345678"
                className={inputCls(false)} onFocus={focusGreen} onBlur={blurGreen(false)} />
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2"><Phone className="w-4 h-4 inline mr-2" />Teléfono 2</label>
              <input type="tel" value={formData.telefono2} onChange={(e) => handleInputChange('telefono2', e.target.value)} placeholder="612345678"
                className={inputCls(false)} onFocus={focusGreen} onBlur={blurGreen(false)} />
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2"><Mail className="w-4 h-4 inline mr-2" />Email</label>
              <input type="email" value={formData.email} onChange={(e) => handleInputChange('email', e.target.value)} placeholder="cliente@empresa.com"
                className={inputCls(errors.email)} onFocus={focusGreen} onBlur={blurGreen(errors.email)} />
              {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2"><Mail className="w-4 h-4 inline mr-2" />Email Admin</label>
              <input type="email" value={formData.mail_admin} onChange={(e) => handleInputChange('mail_admin', e.target.value)} placeholder="admin@empresa.com"
                className={inputCls(errors.mail_admin)} onFocus={focusGreen} onBlur={blurGreen(errors.mail_admin)} />
              {errors.mail_admin && <p className="text-red-500 text-xs mt-1">{errors.mail_admin}</p>}
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2"><Mail className="w-4 h-4 inline mr-2" />Email Admin 2</label>
              <input type="email" value={formData.mail_admin_2} onChange={(e) => handleInputChange('mail_admin_2', e.target.value)} placeholder="admin2@empresa.com"
                className={inputCls(errors.mail_admin_2)} onFocus={focusGreen} onBlur={blurGreen(errors.mail_admin_2)} />
              {errors.mail_admin_2 && <p className="text-red-500 text-xs mt-1">{errors.mail_admin_2}</p>}
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2"><User className="w-4 h-4 inline mr-2" />Persona de Contacto</label>
              <input type="text" value={formData.contacto} onChange={(e) => handleInputChange('contacto', e.target.value)} placeholder="Nombre del contacto principal"
                className={inputCls(false)} onFocus={focusGreen} onBlur={blurGreen(false)} />
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2"><Phone className="w-4 h-4 inline mr-2" />Fax</label>
              <input type="text" value={formData.fax} onChange={(e) => handleInputChange('fax', e.target.value)} placeholder="Número de fax"
                className={inputCls(false)} onFocus={focusGreen} onBlur={blurGreen(false)} />
            </div>

            {/* ── Sincronización de Calendario ───────────────────────────── */}
            <div className="md:col-span-3">
              <h3 className="text-lg font-bold text-gray-800 mb-4 border-b-2 pb-2 mt-4" style={{ borderColor: '#3cbf9a' }}>Sincronización de Calendario</h3>
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2"><Calendar className="w-4 h-4 inline mr-2" />Tipo de Calendario</label>
              <select value={formData.tipo_calendario} onChange={(e) => handleInputChange('tipo_calendario', e.target.value)}
                className="w-full px-3 py-2 border-2 rounded-lg focus:outline-none transition-all bg-white border-gray-300"
                onFocus={focusGreen} onBlur={(e) => (e.target.style.borderColor = '#e5e7eb')}>
                {getTiposCalendario().map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
              <p className="text-xs text-gray-500 mt-1">Sincroniza automáticamente las citas con el calendario externo</p>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-bold text-gray-700 mb-2">
                <Mail className="w-4 h-4 inline mr-2" />Email de Calendario {formData.tipo_calendario !== 'ninguno' && '*'}
              </label>
              <input type="email" value={formData.email_calendario} onChange={(e) => handleInputChange('email_calendario', e.target.value)}
                placeholder={formData.tipo_calendario === 'google' ? 'ejemplo@gmail.com' : formData.tipo_calendario === 'outlook' ? 'ejemplo@outlook.com' : formData.tipo_calendario === 'icloud' ? 'ejemplo@icloud.com' : 'Email del calendario a sincronizar'}
                disabled={formData.tipo_calendario === 'ninguno'}
                className={`w-full px-3 py-2 border-2 rounded-lg focus:outline-none transition-all ${errors.email_calendario ? 'border-red-500' : 'border-gray-300'} ${formData.tipo_calendario === 'ninguno' ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                onFocus={(e) => !errors.email_calendario && formData.tipo_calendario !== 'ninguno' && (e.target.style.borderColor = '#3cbf9a')}
                onBlur={(e) => !errors.email_calendario && (e.target.style.borderColor = '#e5e7eb')} />
              {errors.email_calendario && <p className="text-red-500 text-xs mt-1">{errors.email_calendario}</p>}
              {formData.tipo_calendario !== 'ninguno' && !errors.email_calendario && (
                <p className="text-xs text-gray-500 mt-1">
                  {formData.tipo_calendario === 'google' && 'Debe ser una cuenta @gmail.com'}
                  {formData.tipo_calendario === 'outlook' && 'Debe ser @outlook.com, @hotmail.com o @live.com'}
                  {formData.tipo_calendario === 'icloud' && 'Debe ser @icloud.com o @me.com'}
                </p>
              )}
            </div>

            {/* ── Gestión de Stock ─────────────────────────────────── */}
            <div className="md:col-span-3">
              <h3 className="text-lg font-bold text-gray-800 mb-4 border-b-2 pb-2 mt-4" style={{ borderColor: '#3cbf9a' }}>Gestión de Stock</h3>
            </div>

            <div className="md:col-span-3">
              <label className="block text-sm font-bold text-gray-800 mb-2">Modo de gestión de stock</label>
              <select
                value={formData.gestion_stock || 'inactivo'}
                onChange={(e) => handleInputChange('gestion_stock', e.target.value)}
                className="w-full p-3 rounded-lg border-2 border-gray-200 focus:border-emerald-500 focus:outline-none text-sm"
              >
                <option value="inactivo">Inactivo — sin gestión de stock</option>
                <option value="albaran">Por albarán — añade productos bajo mínimo automáticamente a los pedidos</option>
                <option value="picking">Por escaneo — entradas y salidas con código de barras desde la aplicación móvil</option>
                <option value="mixto">Mixto — albarán con doble verificación (el cliente acepta antes de aplicar al stock) o escaneo cuando no hay albarán</option>
              </select>
              <p className="text-xs text-gray-500 mt-2">
                <span className="font-semibold">Albarán:</span> el cliente lleva inventario y cuando una unidad
                cae bajo el mínimo, aparece automáticamente al hacer un nuevo pedido.
                <br />
                <span className="font-semibold">Escaneo:</span> las entradas y salidas se hacen con código de barras desde la aplicación móvil,
                con control de lotes y fechas de caducidad. La salida sigue el orden FEFO (primero los lotes que caducan antes).
              </p>
            </div>

            {/* ── Aprobación de pedidos ─────────────────────────────────── */}
            <div className="md:col-span-3">
              <label className="flex items-start gap-3 p-3 rounded-lg border-2 border-gray-200 cursor-pointer hover:bg-gray-50">
                <input
                  type="checkbox"
                  checked={!!formData.pedidos_pendientes}
                  onChange={(e) => handleInputChange('pedidos_pendientes', e.target.checked)}
                  className="mt-0.5 w-4 h-4"
                  style={{ accentColor: '#3cbf9a' }}
                />
                <span className="text-sm text-gray-700">
                  <span className="font-bold text-gray-800">Pedidos pendientes de aprobación</span>
                  <br />
                  Si se activa en una <strong>clínica matriz</strong>, los pedidos de sus clínicas hijas
                  pasarán por validación: la matriz deberá aprobarlos desde su pestaña «Pendientes de aprobación».
                  Solo tiene efecto en clínicas matriz (en las hijas se ignora).
                </span>
              </label>
            </div>

            {/* ── Gestión de albaranes ──────────────────────────────────── */}
            <div className="md:col-span-3">
              <label className="flex items-start gap-3 p-3 rounded-lg border-2 border-gray-200 cursor-pointer hover:bg-gray-50">
                <input
                  type="checkbox"
                  checked={!!formData.gestion_albaranes}
                  onChange={(e) => handleInputChange('gestion_albaranes', e.target.checked)}
                  className="mt-0.5 w-4 h-4"
                  style={{ accentColor: '#3cbf9a' }}
                />
                <span className="text-sm text-gray-700">
                  <span className="font-bold text-gray-800">Gestión de albaranes</span>
                  <br />
                  Si se activa, el cliente verá la pestaña de albaranes con el listado de pedidos
                  recibidos y la descarga en .zip. Si se activa en una <strong>clínica matriz</strong>,
                  todas sus clínicas hijas la tendrán activada automáticamente.
                </span>
              </label>
            </div>

            {/* ── Notificaciones de incidencias ─────────────────────────── */}
            <div className="md:col-span-3">
              <label className="flex items-start gap-3 p-3 rounded-lg border-2 border-gray-200 cursor-pointer hover:bg-gray-50">
                <input
                  type="checkbox"
                  checked={!!formData.notif_incidencias}
                  onChange={(e) => handleInputChange('notif_incidencias', e.target.checked)}
                  className="mt-0.5 w-4 h-4"
                  style={{ accentColor: '#3cbf9a' }}
                />
                <span className="text-sm text-gray-700">
                  <span className="font-bold text-gray-800">Notificaciones de incidencias por email</span>
                  <br />
                  Si se activa, al registrar una incidencia se envía un aviso por email al equipo interno
                  (soporte) y a la <strong>clínica matriz</strong>. Desactívalo para silenciar esos correos
                  en este cliente.
                </span>
              </label>
            </div>

            {/* ── Límite de gasto del cliente ───────────────────────────── */}
            <div className="md:col-span-3">
              <label className="block text-sm font-bold text-gray-800 mb-2">
                <CreditCard className="w-4 h-4 inline mr-2" />
                Límite de gasto por pedido (opcional)
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Desde (€)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.importe_min}
                    onChange={(e) => handleInputChange('importe_min', e.target.value)}
                    placeholder="0.00"
                    className="w-full p-3 rounded-lg border-2 border-gray-200 focus:border-emerald-500 focus:outline-none text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Hasta (€)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.importe_max}
                    onChange={(e) => handleInputChange('importe_max', e.target.value)}
                    placeholder="∞ sin techo"
                    className="w-full p-3 rounded-lg border-2 border-gray-200 focus:border-emerald-500 focus:outline-none text-sm"
                  />
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Techo de importe (€, IVA incluido) que este cliente puede pedir <strong>sin aprobación</strong>.
                Si un pedido supera el máximo, <strong>no se bloquea</strong>: pasa a «Pendientes de aprobación»
                para que lo valide el supervisor. Dejá «Hasta» vacío para «sin techo».
                <br />
                Aplica a toda la clínica; si un usuario tiene su propio límite configurado, el suyo tiene prioridad.
              </p>
            </div>

            {/* ── Información Financiera ─────────────────────────────────── */}
            <div className="md:col-span-3">
              <h3 className="text-lg font-bold text-gray-800 mb-4 border-b-2 pb-2 mt-4" style={{ borderColor: '#3cbf9a' }}>Información Financiera</h3>
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2"><CreditCard className="w-4 h-4 inline mr-2" />IBAN</label>
              <input type="text" value={formData.iban} onChange={(e) => handleInputChange('iban', e.target.value)} placeholder="ES9121000418450200051332"
                className={inputCls(false)} onFocus={focusGreen} onBlur={blurGreen(false)} />
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2"><CreditCard className="w-4 h-4 inline mr-2" />Forma de Pago</label>
              <select value={formData.f_pago} onChange={(e) => handleInputChange('f_pago', e.target.value)}
                className="w-full px-3 py-2 border-2 rounded-lg focus:outline-none transition-all bg-white border-gray-300"
                onFocus={focusGreen} onBlur={(e) => (e.target.style.borderColor = '#e5e7eb')}>
                <option value="">Seleccionar forma de pago</option>
                {formasPago.map(f => <option key={f.id} value={f.id}>{f.descripcion}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2"><Calendar className="w-4 h-4 inline mr-2" />Día de Pago</label>
              <input type="text" value={formData.dia_pago} onChange={(e) => handleInputChange('dia_pago', e.target.value)} placeholder="Ej: 1-15 de cada mes"
                className={inputCls(false)} onFocus={focusGreen} onBlur={blurGreen(false)} />
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2"><Calculator className="w-4 h-4 inline mr-2" />Cuenta Contable</label>
              <input type="text" value={formData.cuenta_contable} onChange={(e) => handleInputChange('cuenta_contable', e.target.value)} placeholder="Código de cuenta contable"
                className={inputCls(false)} onFocus={focusGreen} onBlur={blurGreen(false)} />
            </div>

            {/* ── Modelo de Negocio ─────────────────────────────────────── */}
            <div className="md:col-span-3">
              <h3 className="text-lg font-bold text-gray-800 mb-4 border-b-2 pb-2 mt-4" style={{ borderColor: '#3cbf9a' }}>Modelo de Negocio</h3>
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2"><Handshake className="w-4 h-4 inline mr-2" />Modelo de Negocio</label>
              <select value={formData.modelo_negocio} onChange={(e) => handleInputChange('modelo_negocio', e.target.value)}
                className="w-full px-3 py-2 border-2 rounded-lg focus:outline-none transition-all bg-white border-gray-300"
                onFocus={focusGreen} onBlur={(e) => (e.target.style.borderColor = '#e5e7eb')}>
                <option value="">Seleccionar modelo</option>
                {getModelosNegocio().map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2"><Percent className="w-4 h-4 inline mr-2" />Comisión (%)</label>
              <input type="number" step="0.01" value={formData.comision} onChange={(e) => handleInputChange('comision', e.target.value)} placeholder="0.00"
                className={inputCls(false)} onFocus={focusGreen} onBlur={blurGreen(false)} />
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2"><Percent className="w-4 h-4 inline mr-2" />Porcentaje Ahorro (%)</label>
              <input type="number" step="0.01" value={formData.ahorro} onChange={(e) => handleInputChange('ahorro', e.target.value)} placeholder="0.00"
                className={inputCls(false)} onFocus={focusGreen} onBlur={blurGreen(false)} />
            </div>
            <div className="md:col-span-3">
              <label className="block text-sm font-bold text-gray-700 mb-2"><FileText className="w-4 h-4 inline mr-2" />Detalle del Modelo de Negocio</label>
              <textarea value={formData.modneg_detalle} onChange={(e) => handleInputChange('modneg_detalle', e.target.value)} placeholder="Descripción detallada del modelo de negocio" rows="3"
                className="w-full px-3 py-2 border-2 rounded-lg focus:outline-none transition-all border-gray-300"
                onFocus={focusGreen} onBlur={(e) => (e.target.style.borderColor = '#e5e7eb')} />
            </div>

            {/* ── Relaciones ────────────────────────────────────────────── */}
            <div className="md:col-span-3">
              <h3 className="text-lg font-bold text-gray-800 mb-4 border-b-2 pb-2 mt-4" style={{ borderColor: '#3cbf9a' }}>Relaciones</h3>
            </div>

            <div className="relative md:col-span-3">
              <label className="block text-sm font-bold text-gray-700 mb-2"><Building className="w-4 h-4 inline mr-2" />Clínica Matriz</label>
              <input type="text" value={formData.clinica_matriz} onChange={(e) => handleInputChange('clinica_matriz', e.target.value)} placeholder="Buscar clínica matriz..."
                className="w-full px-3 py-2 border-2 rounded-lg focus:outline-none transition-all bg-white border-gray-300"
                onFocus={focusGreen}
                onBlur={(e) => { e.target.style.borderColor = '#e5e7eb'; setTimeout(() => setShowClientSuggestions(false), 200); }} />
              {showClientSuggestions && filteredClients.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border-2 rounded-lg shadow-lg max-h-48 overflow-y-auto" style={{ borderColor: '#3cbf9a' }}>
                  {filteredClients.slice(0, 5).map(client => (
                    <div key={client.id} onClick={() => { handleInputChange('clinica_matriz', client.razon_social); setShowClientSuggestions(false); }}
                      className="px-3 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0">
                      <div className="font-medium text-gray-900">{client.razon_social}</div>
                      {client.cif && <div className="text-sm text-gray-500">CIF: {client.cif}</div>}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* ── Personalización Visual ────────────────────────────────── */}
            <div className="md:col-span-3">
              <h3 className="text-lg font-bold text-gray-800 mb-1 border-b-2 pb-2 mt-4 flex items-center" style={{ borderColor: '#3cbf9a' }}>
                <Palette className="w-5 h-5 mr-2" style={{ color: '#3cbf9a' }} />
                Personalización Visual
              </h3>
              <p className="text-sm text-gray-500 mb-4">
                Configura los colores de la interfaz para este cliente y todos sus centros asociados.
              </p>
            </div>

            {/* Color Primario */}
            <div>
              <ColorPickerField
                label="Color Primario"
                value={formData.color_primario}
                defaultColor="#3cbf9a"
                onChange={(v) => handleInputChange('color_primario', v)}
                helpText="Botones principales, cabeceras y elementos activos"
              />
              {errors.color_primario && <p className="text-red-500 text-xs mt-1">{errors.color_primario}</p>}
            </div>

            {/* Color Secundario */}
            <div>
              <ColorPickerField
                label="Color Secundario"
                value={formData.color_secundario}
                defaultColor="#1a73e8"
                onChange={(v) => handleInputChange('color_secundario', v)}
                helpText="Acentos, badges de 'Matriz' y elementos de apoyo"
              />
              {errors.color_secundario && <p className="text-red-500 text-xs mt-1">{errors.color_secundario}</p>}
            </div>

            {/* Vista previa en tiempo real */}
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Vista Previa</label>
              <div className="border-2 border-gray-200 rounded-xl overflow-hidden shadow-sm">
                <div className="px-4 py-3 text-white text-sm font-semibold flex items-center justify-between" style={{ backgroundColor: isValidHex(formData.color_primario) ? formData.color_primario : '#3cbf9a' }}>
                  <span>Cabecera</span>
                  <div className="flex gap-1">{[1,2,3].map(i => <div key={i} className="w-2 h-2 rounded-full bg-white opacity-60" />)}</div>
                </div>
                <div className="p-3 bg-gray-50 space-y-2">
                  <button type="button" className="w-full text-white text-xs font-medium py-1.5 rounded-lg" style={{ backgroundColor: isValidHex(formData.color_primario) ? formData.color_primario : '#3cbf9a' }}>
                    Botón primario
                  </button>
                  <button type="button" className="w-full text-white text-xs font-medium py-1.5 rounded-lg" style={{ backgroundColor: isValidHex(formData.color_secundario) ? formData.color_secundario : '#1a73e8' }}>
                    Botón secundario
                  </button>
                  <div className="flex gap-2 flex-wrap">
                    <span className="text-xs px-2 py-1 rounded-full font-medium" style={{ backgroundColor: `${isValidHex(formData.color_primario) ? formData.color_primario : '#3cbf9a'}20`, color: isValidHex(formData.color_primario) ? formData.color_primario : '#3cbf9a' }}>Activo</span>
                    <span className="text-xs px-2 py-1 rounded-full font-medium" style={{ backgroundColor: `${isValidHex(formData.color_secundario) ? formData.color_secundario : '#1a73e8'}20`, color: isValidHex(formData.color_secundario) ? formData.color_secundario : '#1a73e8' }}>Matriz</span>
                  </div>
                </div>
              </div>
            </div>

          </div>

          {/* ── Botón Crear ────────────────────────────────────────────────── */}
          <div className="flex justify-center gap-4 mt-8">
            <button
              onClick={handleSubmit}
              disabled={saving || loadingNextId}
              className={`px-6 py-3 rounded-lg font-medium transition-all flex items-center text-white ${saving || loadingNextId ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-90'}`}
              style={{ backgroundColor: '#3cbf9a' }}
            >
              {saving ? (
                <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>Creando...</>
              ) : (
                <><Save className="w-4 h-4 mr-2" />Crear Cliente</>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}